import { useCallback, useEffect, useId, useRef, useState } from 'react'

/** One measured line: its points are [unix seconds, value], oldest first. */
export interface Line { label: string; color: string; points: [number, number][]; dashed?: boolean }

const HEIGHT = 96
const MAX_TICKS = 4

/** The axis top: the next round number at or above the peak (as ColumnChart does). */
function niceMax(peak: number) {
  if (peak <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(peak))
  for (const step of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (peak <= step * magnitude) return step * magnitude
  }
  return 10 * magnitude
}

/**
 * A few measures over the same time, on one scale: 2px lines drawn point to point (a
 * curve would suggest values nobody measured), a gap where a line has no data, and a
 * crosshair that reads every line at the hovered time. Sized like ColumnChart and
 * using its axis, tooltip and empty-state styles, so the two sit side by side.
 * A reading with no neighbour is a dot, so a sparse measure still shows. From
 * `partialFrom` on, a bucket is still filling: its part of a line is dashed and its
 * dot hollow, and the tooltip says "so far".
 */
export function LineChart({ lines, format, tick, caption, emptyLabel, max, partialFrom, compact = false }: {
  lines: Line[]
  format: (value: number) => string
  /** Short axis label for a time, e.g. "16:05". */
  tick: (unix: number) => string
  /** A time in the tooltip, e.g. "16 Oct, 16:05". */
  caption: (unix: number) => string
  emptyLabel: string
  /** A fixed top for the axis, e.g. 100 for percentages. */
  max?: number
  /** Unix seconds from which buckets are incomplete (the current one, so far). */
  partialFrom?: number
  compact?: boolean
}) {
  const [hover, setHover] = useState<number>()
  const tooltipId = useId()
  const [plot, setPlot] = useState(0)
  const observer = useRef<ResizeObserver>()
  const box = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    if (!node) return
    observer.current = new ResizeObserver(([entry]) => setPlot(entry.contentRect.width))
    observer.current.observe(node)
    setPlot(node.clientWidth)
  }, [])
  useEffect(() => () => observer.current?.disconnect(), [])

  const drawn = lines.filter(line => line.points.length)
  const times = [...new Set(drawn.flatMap(line => line.points.map(([t]) => t)))].sort((a, b) => a - b)
  if (!times.length) return <p className={`chart-empty${compact ? ' compact' : ''}`}>{emptyLabel}</p>

  const height = compact ? 56 : HEIGHT
  const peak = Math.max(0, ...drawn.flatMap(line => line.points.map(([, v]) => v)))
  const axis = max ?? niceMax(peak)
  const first = times[0], last = times[times.length - 1]
  const span = Math.max(1, last - first)
  const x = (t: number) => times.length === 1 ? plot / 2 : ((t - first) / span) * plot
  const y = (v: number) => height - (Math.min(v, axis) / axis) * height
  // A line breaks where it has no point for longer than its usual step.
  const step = times.length > 1 ? Math.min(...times.slice(1).map((t, i) => t - times[i])) : 60
  const near = (a: number, b: number) => Math.abs(a - b) <= step * 1.5
  const path = (points: [number, number][]) => points.map(([t, v], i) =>
    `${i && near(t, points[i - 1][0]) ? 'L' : 'M'}${x(t).toFixed(1)},${y(v).toFixed(1)}`).join('')
  const partial = (t: number) => partialFrom != null && t >= partialFrom
  // The complete part, and the partial part joined to the last complete point before it.
  const parts = (points: [number, number][]) => {
    const from = points.findIndex(([t]) => partial(t))
    return from < 0 ? { done: points, open: [] } : { done: points.slice(0, from), open: points.slice(Math.max(0, from - 1)) }
  }
  const lonely = (points: [number, number][]) => points.filter(([t], i) =>
    !(i && near(t, points[i - 1][0])) && !(i < points.length - 1 && near(points[i + 1][0], t)))
  const ticks = compact ? [first, last] : [...new Set([...Array(MAX_TICKS).keys()].map(i =>
    times[Math.round(i * (times.length - 1) / (MAX_TICKS - 1))]))]
  const at = hover != null ? times[hover] : undefined
  const values = at == null ? [] : drawn.map(line => ({ line, value: line.points.find(([t]) => t === at)?.[1] }))
  const tooltipWidth = Math.min(230, plot)
  const tooltipLeft = at == null ? 0 : Math.min(plot - tooltipWidth, Math.max(0, x(at) - tooltipWidth / 2))
  const nearest = (clientX: number, rect: DOMRect) => {
    const t = first + ((clientX - rect.left) / rect.width) * span
    let best = 0
    times.forEach((time, i) => { if (Math.abs(time - t) < Math.abs(times[best] - t)) best = i })
    return best
  }

  return <div className={`column-chart line-chart${compact ? ' compact' : ''}`}>
    <div className="chart-plot">
      <div className="y-axis" aria-hidden="true" style={{ height }}><span>{format(axis)}</span><span>0</span></div>
      <div className="chart-area" ref={box}>
        {plot > 0 && <svg viewBox={`0 0 ${plot} ${height}`} preserveAspectRatio="none" style={{ height }} role="img" tabIndex={0}
          aria-label={`${drawn.map(line => line.label).join(', ')}; latest ${drawn.map(line => `${line.label} ${format(line.points[line.points.length - 1][1])}`).join(', ')}`}
          aria-describedby={hover != null ? tooltipId : undefined}
          onPointerMove={event => setHover(nearest(event.clientX, event.currentTarget.getBoundingClientRect()))}
          onPointerLeave={() => setHover(undefined)}
          onKeyDown={event => {
            if (event.key === 'ArrowRight') setHover(h => Math.min(times.length - 1, (h ?? -1) + 1))
            if (event.key === 'ArrowLeft') setHover(h => Math.max(0, (h ?? times.length) - 1))
            if (event.key === 'Escape') setHover(undefined)
          }} onBlur={() => setHover(undefined)}>
          {[0.5].map(f => <line key={f} x1="0" x2={plot} y1={height * f} y2={height * f} className="line-grid" />)}
          {drawn.map(line => {
            const { done, open } = parts(line.points)
            return <g key={line.label}>
              <path d={path(done)} fill="none" stroke={line.color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" strokeDasharray={line.dashed ? '4 3' : undefined}
                vectorEffect="non-scaling-stroke" />
              {open.length > 1 && <path d={path(open)} fill="none" stroke={line.color} strokeWidth="2" className="line-partial"
                strokeLinecap="round" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />}
              {lonely(line.points).map(([t, v]) => <circle key={t} cx={x(t)} cy={y(v)} r="2.5"
                className={partial(t) ? 'line-lonely partial' : 'line-lonely'} style={{ fill: partial(t) ? undefined : line.color, stroke: line.color }} />)}
            </g>
          })}
          {at != null && <line x1={x(at)} x2={x(at)} y1="0" y2={height} className="line-cursor" />}
          {at != null && values.map(({ line, value }) => value == null ? null :
            <circle key={line.label} cx={x(at)} cy={y(value)} r="3.5" fill={line.color} className="line-dot" />)}
        </svg>}
        {at != null && <div className="chart-tooltip" id={tooltipId} role="tooltip"
          style={{ left: tooltipLeft, top: 0, width: tooltipWidth }}>
          <span className="chart-tooltip-time">{caption(at)}{partial(at) && ' · so far'}</span>
          {drawn.length === 1
            ? <strong>{values[0].value == null ? '—' : format(values[0].value)}<small> {drawn[0].label.toLowerCase()}</small></strong>
            : <dl>{values.map(({ line, value }) => <div key={line.label}>
              <dt><i style={{ background: line.color }} />{line.label}</dt><dd>{value == null ? '—' : format(value)}</dd>
            </div>)}</dl>}
        </div>}
        <div className="x-axis" aria-hidden="true">{ticks.map((t, i) => <span className="x-tick" key={t} style={i === 0
          ? { left: 0 } : i === ticks.length - 1 ? { right: 0 } : { left: `${(x(t) / Math.max(1, plot)) * 100}%`, transform: 'translateX(-50%)' }}>
          {tick(t)}</span>)}</div>
      </div>
    </div>
  </div>
}

/** The legend under a chart: a swatch, the name and its latest value. */
export function Legend({ lines, format }: { lines: Line[]; format: (value: number) => string }) {
  return <ul className="chart-legend">{lines.map(line => {
    const latest = line.points[line.points.length - 1]?.[1]
    return <li key={line.label}><i style={{ background: line.color }} className={line.dashed ? 'dashed' : ''} />
      <span>{line.label}</span><strong>{latest == null ? '—' : format(latest)}</strong></li>
  })}</ul>
}
